// ============================================================================
// ProfileForm (L16, F-839..F-844 + M-804/M-805) — auto-edition forces + Nova
// ============================================================================
// Portage de (dashboard)/profile/profile-form.tsx. M-807 : AUCUN ciblage — tape
// /api/profile/me/strengths, le serveur prend l'email du TOKEN.
//
// F-839 : 5 selecteurs Gallup groupes par domaine (GALLUP_STRENGTHS_BY_DOMAIN).
// F-840 : detection de doublon LIVE, AVANT submit (affordance ; le serveur
//         revalide — M-804 est verrouille des deux cotes, cf. profile.a3.test.ts).
// F-842 : 4 champs % Nova + jauge mise a jour en DIRECT (etat local).
// F-843 : bouton desactive si `pending` OU doublon detecte.
// M-804 : une selection partiellement/totalement vide est AUTORISEE (les positions
//         vides sont compactees) — ne jamais forcer les 5.
// M-805 : entier 0-100 par couleur, '' -> null, AUCUNE contrainte de somme.

import { useState } from 'react';
import { GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { GALLUP_STRENGTHS_BY_DOMAIN, type GallupDomain } from '../../lib/profile/gallup';
import { NOVA_COLORS, NOVA_BAR_CLASSES, type NovaColor } from '../../lib/profile/nova';
import { useUpdateMyStrengths, profileErrorCode, type NovaScores } from '../../hooks/useProfile';

const SLOTS = [0, 1, 2, 3, 4] as const;

/** M-805 : '' -> null ; sinon entier borne 0-100 (le serveur revalide). */
function parseNova(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 && n <= 100 ? n : null;
}

export interface ProfileFormProps {
  initialStrengths: string[];
  initialNova: NovaScores;
}

export function ProfileForm({ initialStrengths, initialNova }: ProfileFormProps) {
  const { t } = useTranslation();
  const update = useUpdateMyStrengths();

  const [strengths, setStrengths] = useState<string[]>(() =>
    SLOTS.map((i) => initialStrengths[i] ?? ''),
  );
  const [nova, setNova] = useState<Record<NovaColor, string>>(() => ({
    red: initialNova.red?.toString() ?? '',
    yellow: initialNova.yellow?.toString() ?? '',
    green: initialNova.green?.toString() ?? '',
    blue: initialNova.blue?.toString() ?? '',
  }));

  // F-840 : premiere force selectionnee 2x (les positions vides ne comptent pas).
  const duplicateStrength = (() => {
    const seen = new Set<string>();
    for (const s of strengths) {
      if (!s) continue;
      if (seen.has(s)) return s;
      seen.add(s);
    }
    return null;
  })();

  const setSlot = (i: number, value: string) => {
    setStrengths((prev) => prev.map((s, idx) => (idx === i ? value : s)));
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (duplicateStrength) return;
    update.mutate({
      // M-804 : compactage des positions vides (selection partielle autorisee).
      gallupStrengths: strengths.filter((s) => s.length > 0),
      nova: {
        red: parseNova(nova.red),
        yellow: parseNova(nova.yellow),
        green: parseNova(nova.green),
        blue: parseNova(nova.blue),
      },
    });
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      {/* F-839 : 5 selecteurs, options groupees par domaine Gallup. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {SLOTS.map((i) => (
          <div key={i} className="flex flex-col gap-1">
            <label htmlFor={`gallup-${i}`} className="text-meta font-medium text-slate-600 dark:text-slate-300">
              {t('gest:profile.form.gallupSlot', { rank: i + 1 })}
            </label>
            <select
              id={`gallup-${i}`}
              value={strengths[i]}
              onChange={(e) => setSlot(i, e.target.value)}
              className={GS_COMPONENTS.input}
            >
              <option value="">{t('gest:profile.form.gallupNone')}</option>
              {/* Les DOMAINES sont traduits ; les 34 THEMES restent en libelle
                  Gallup FR officiel — ils sont la VALEUR persistee (M-804 les
                  valide contre le catalogue), les traduire casserait la donnee. */}
              {(Object.keys(GALLUP_STRENGTHS_BY_DOMAIN) as GallupDomain[]).map((domain) => (
                <optgroup key={domain} label={t(`gest:profile.gallupDomains.${domain}`)}>
                  {GALLUP_STRENGTHS_BY_DOMAIN[domain].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        ))}
      </div>

      {/* F-840 : alerte doublon AVANT submit. */}
      {duplicateStrength ? (
        <p role="alert" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-field text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
          {t('gest:profile.form.duplicate', { strength: duplicateStrength })}
        </p>
      ) : null}

      {/* F-842 : 4 pourcentages Nova + jauge live. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {NOVA_COLORS.map((color) => (
          <div key={color} className="flex flex-col gap-1">
            <label htmlFor={`nova-${color}`} className="text-meta font-medium text-slate-600 dark:text-slate-300">
              {t(`gest:profile.nova.${color}.label`)}
            </label>
            <input
              id={`nova-${color}`}
              type="number"
              min={0}
              max={100}
              step={1}
              value={nova[color]}
              onChange={(e) => setNova((prev) => ({ ...prev, [color]: e.target.value }))}
              className={GS_COMPONENTS.input}
            />
            <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
              <div
                className={`h-full rounded-full transition-all ${NOVA_BAR_CLASSES[color]}`}
                style={{ width: `${parseNova(nova[color]) ?? 0}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        {/* F-843 : desactive si en cours OU doublon. */}
        <button
          type="submit"
          disabled={update.isPending || duplicateStrength !== null}
          className={`${GS_COMPONENTS.btnAction} touch-manipulation disabled:opacity-50`}
        >
          {t('gest:profile.form.save')}
        </button>
        {/* F-841/F-844 : resultat serveur (code court -> message i18n, GA-21). */}
        {update.isSuccess ? (
          <p role="status" className="text-meta text-emerald-700 dark:text-emerald-400">
            {t('gest:profile.form.saved')}
          </p>
        ) : null}
        {update.isError ? (
          <p role="alert" className="text-meta text-red-700 dark:text-red-400">
            {t(`gest:profile.errors.${profileErrorCode(update.error)}`)}
          </p>
        ) : null}
      </div>
    </form>
  );
}
